import React, { useCallback, useEffect, useMemo } from "react"
import PropTypes from "prop-types"
import { useLocation } from "react-router-dom"
import { Button, Dropdown, Flex, Tabs } from "antd"
import { DownloadOutlined, PlusOutlined } from "@ant-design/icons"
import { useDispatch } from "react-redux"
import useActiveGuide from "hooks/useActiveClient"
import { BreadCrumbs } from "ui/BreadCrumbs"
import { useTranslation } from "react-i18next"
import { api } from "store/helpers/api"
import { cleanExpenses, getGuideAccounting, getGuideAccountingOverview, getGuideTipReport } from "store/accounting"
import { DEFAULT_RANGE, DEFAULT_TYPE, SCOUP_LIST, TYPE_OPTIONS_ENUM, TYPE_WITH_DATESWITCHER } from "constants/accounting"
import FormAntdModal from "./FormAntdModal"
import styles from "./Header.module.css"

function buildDateQuery(params) {
  if (params.date_gteq && params.date_lteq) {
    return { date_gteq: params.date_gteq, date_lteq: params.date_lteq }
  }
  if (params.date && params.range) {
    const range = params.range === "week" ? "weekDay" : params.range
    const date = global.dateTime(params.date)
    return { date_gteq: date?.startOf(range, 1)?.format(), date_lteq: date?.endOf(range, 1)?.format() }
  }
  return null
}

function buildTipReportParams(params, q) {
  const crewIds = Array.isArray(params["crew_ids[]"]) ? params["crew_ids[]"] : []
  if (crewIds.length) q.crew_profile_id_in = crewIds
  if (params.include_unassigned) q.crew_profile_id_null = true
  return { include_stats: true, q }
}

function buildSalesParams(searchParams, params) {
  if (params.payment_type) searchParams.payment_type = params.payment_type
  if (params.booking_type) searchParams.booking_type = params.booking_type
  if (params.transaction_type) searchParams.transaction_type = params.transaction_type
  if (params.experience) searchParams.experience = params.experience
  return searchParams
}

function buildTypeSpecificParams(searchParams, params) {
  if (params.type === "sales") return buildSalesParams(searchParams, params)
  if (params.type === "expenses" && params.category_id) searchParams.category_id = params.category_id
  return searchParams
}

export const requestParams = (params) => {
  if (!params?.type) return undefined
  const q = buildDateQuery(params)
  if (!q) return undefined

  if (params.type === "tip_report") return buildTipReportParams(params, q)

  const searchParams = { include_stats: true, q }
  if (SCOUP_LIST?.includes(params.type)) searchParams.scope = params.type.toLowerCase()
  return buildTypeSpecificParams(searchParams, params)
}

function buildExportFilename(downloadType, type) {
  if (downloadType && typeof downloadType === "string") return `${downloadType}_report.csv`
  if (type === "expenses" || type === "sales" || type === "tip_report") return `${type}_report.csv`
  return "report.csv"
}

function buildExportParams(params, downloadType) {
  let exportParams = requestParams(params)
  if (!exportParams) {
    exportParams = {}
    if (params?.date_gteq && params?.date_lteq) {
      exportParams.q = { date_gteq: params.date_gteq, date_lteq: params.date_lteq }
    }
  }
  if (downloadType && typeof downloadType === "string" && params?.type === "business_overview") {
    exportParams.scope = downloadType
  }
  return exportParams
}

Header.propTypes = {
  params: PropTypes.shape({
    type: PropTypes.string,
    date: PropTypes.string,
    range: PropTypes.string,
    date_gteq: PropTypes.string,
    date_lteq: PropTypes.string,
    include_unassigned: PropTypes.bool,
    "crew_ids[]": PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))
  }).isRequired,
  onChangeFilters: PropTypes.func.isRequired
}

export default function Header({ params, onChangeFilters }) {
  const [visible, setVisible] = React.useState(false)
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const location = useLocation()
  const { isActive: active, isPending } = useActiveGuide()
  const showDateSwitcher = TYPE_WITH_DATESWITCHER?.includes(params?.type)
  const needReload = location?.state?.reload || false

  const changeTypeHandler = useCallback(
    (type) => {
      const hasDateswitcher = TYPE_WITH_DATESWITCHER?.includes(type)
      const date = hasDateswitcher ? (params?.date || global.dateTime().startOf("date").format()) : null
      onChangeFilters({ target: { value: type, name: "type" } })
      onChangeFilters({ target: { value: date, name: "date" } })
      if (!date) onChangeFilters({ target: { value: null, name: "range" } })
    },
    [params?.date, onChangeFilters]
  )

  useEffect(() => {
    if (showDateSwitcher && !params?.range) onChangeFilters({ target: { value: DEFAULT_RANGE, name: "range" } })
    else if (!showDateSwitcher && params?.range) onChangeFilters({ target: { value: null, name: "range" } })
  }, [params?.range, showDateSwitcher]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!params?.type) changeTypeHandler(DEFAULT_TYPE)
  }, [params?.type, changeTypeHandler])

  const fetchAll = useCallback(() => {
    if (!params?.type) return
    const searchParams = requestParams(params)
    if (!searchParams) return
    dispatch(getGuideAccounting(searchParams))
  }, [dispatch, params])

  const fetchOverview = useCallback(() => {
    if (!params?.type) return
    const searchParams = {}
    if (params?.date_gteq && params?.date_lteq) searchParams.q = { date_gteq: params.date_gteq, date_lteq: params.date_lteq }
    dispatch(getGuideAccountingOverview(searchParams))
  }, [dispatch, params])

  const getAccountingDataHandler = useCallback(
    async (downloadType) => {
      const exportParams = buildExportParams(params, downloadType)
      const exportUrl = params?.type === "tip_report" ? "/guide/accounting/tip_report/export" : "/guide/accounting/export"
      const filename = buildExportFilename(downloadType, params?.type)
      dispatch(api({ url: exportUrl, params: exportParams })).then((data) => {
        const blob = new Blob([data], { type: "text/csv" })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement("a")
        link.href = url
        link.setAttribute("download", filename)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      })
    },
    [dispatch, params]
  )

  const fetchData = useCallback(() => {
    if (!params?.type) return
    if (params.type === "tip_report") {
      const searchParams = requestParams(params)
      if (searchParams) dispatch(getGuideTipReport(searchParams))
      return
    }
    if (params.type === "business_overview") fetchOverview()
    else fetchAll()
  }, [dispatch, params, fetchAll, fetchOverview])

  useEffect(() => {
    fetchData()
  }, [params?.type, params?.range, params?.date, params?.date_gteq, params?.date_lteq, params?.["crew_ids[]"], params?.include_unassigned]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => dispatch(cleanExpenses()), []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!needReload) return
    window.history.replaceState({}, document.title)
    fetchData()
  }, [needReload]) // eslint-disable-line react-hooks/exhaustive-deps

  const tabItems = useMemo(
    () =>
      Object.entries(TYPE_OPTIONS_ENUM || {}).map(([type, label]) => ({
        key: type,
        label
      })),
    []
  )

  const downloadMenu = useMemo(
    () => [
      { key: "expenses", label: t("accounting.download_expenses") },
      { key: "sales", label: t("accounting.download_sales") }
    ],
    [t]
  )

  const handleDownloadMenuClick = useCallback(({ key }) => {
    if (key) getAccountingDataHandler(key)
  }, [getAccountingDataHandler])

  const handleOpenModal = useCallback(() => setVisible(true), [])
  const handleCloseModal = useCallback(() => setVisible(false), [])
  const handleDownloadAll = useCallback(() => getAccountingDataHandler(), [getAccountingDataHandler])

  return (
    <Flex vertical gap={18} className={styles.header}>
      <Flex justify="space-between" align="center" wrap gap={18} className={styles.topRow}>
        <BreadCrumbs iconName="expense" category={t("accounting.breadcrumb")} />

        <Flex wrap gap={12} className={styles.actions}>
          {params?.type !== "tip_report" && (
            <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenModal} disabled={isPending} className={styles.addButton}>
              {t("expense.add")}
            </Button>
          )}

          {params?.type === "business_overview" ? (
            <Dropdown menu={{ items: downloadMenu, onClick: handleDownloadMenuClick }} placement="bottomRight" trigger={["click"]}>
              <Button icon={<DownloadOutlined />} className={styles.downloadButton}>
                {t("accounting.download_csv")}
              </Button>
            </Dropdown>
          ) : (
            <Button icon={<DownloadOutlined />} className={styles.downloadButton} onClick={handleDownloadAll}>
              {t("accounting.download_csv")}
            </Button>
          )}
        </Flex>
      </Flex>

      <Flex className={styles.tabsRow}>
        <Tabs
          activeKey={params?.type || DEFAULT_TYPE}
          onChange={changeTypeHandler}
          className={styles.tabs}
          tabBarStyle={{ margin: 0, padding: 0 }}
          type="line"
          items={tabItems}
        />
      </Flex>

      {visible && <FormAntdModal setVisible={setVisible} visible={visible} onClose={handleCloseModal} onSuccess={fetchData} />}
    </Flex>
  )
}
